import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import type { Session } from "@training/shared";
import { useTimer } from "../hooks/useTimer.ts";
import { chime } from "../lib/chime.ts";
import { BACK_TO_IT_SECONDS, restRemaining } from "../lib/rest.ts";
import { BACK_TO_IT } from "./RestTimer.tsx";
import { resumePath } from "../lib/resume.ts";
import { RestRing } from "./RestRing.tsx";

/**
 * The session under way, in a pill over every screen but the movement's own: a look
 * at the journal mid-rest no longer loses the countdown. On the session's
 * own list, where its name is already the title, only the rest remains.
 *
 * Ticks once a second, not four times: the screen stays on for the whole
 * session and this is only glanced at.
 *
 * Sets still waiting for the server are counted in it (`pending`): the phone
 * has no sidebar to say so, and a set parked offline that nobody sees is one
 * nobody thinks to send before closing the app.
 */
export function LiveBar({
  session,
  onSessionScreen,
  home,
  pending,
}: {
  session: Session;
  onSessionScreen: boolean;
  /** The home screen: its card of the session under way leads back already. */
  home: boolean;
  /** "2 en attente", or null once all is sent. */
  pending: string | null;
}) {
  const timer = useTimer(session.id);
  const [now, setNow] = useState(() => Date.now());
  const announced = useRef<number | null>(null);
  const resting = timer.startedAt !== null;

  useEffect(() => {
    if (!resting) return;
    const update = () => setNow(Date.now());
    // At once, then every second: the clock must not open on a stale time.
    const first = setTimeout(update, 0);
    const tick = setInterval(update, 1000);
    return () => {
      clearTimeout(first);
      clearInterval(tick);
    };
  }, [resting, timer.startedAt]);

  const left = resting ? restRemaining({ startedAt: timer.startedAt!, seconds: timer.seconds }, now) : null;
  const over = left !== null && left <= 0;
  const expired = left !== null && left < -BACK_TO_IT_SECONDS;

  // Back at it by now: the rest closes itself, no overtime counted.
  const { stop } = timer;
  useEffect(() => {
    if (expired) stop();
  }, [expired, stop]);

  // The end is announced here when the movement's screen, which would
  // otherwise do it, is not the one open. Once per rest.
  useEffect(() => {
    if (over && announced.current !== timer.startedAt) {
      announced.current = timer.startedAt;
      chime();
    }
  }, [over, timer.startedAt]);

  // Where the screen already leads back to the session, the pill only
  // stays for what it alone says: the rest, and sets waiting to be sent.
  if ((onSessionScreen || (home && !pending)) && (!resting || expired)) return null;

  // A pill up in the corner, the way the phone shows a call under way: a
  // band across the bottom sat over the content of every page, and right
  // under the thumb. Up here it is in the eye's way, not the hand's.
  const pill = [
    "fixed right-3 z-40 flex h-11 items-center gap-2 rounded-full border bg-[#0b0d10]/95 pr-1 pl-1.5 shadow-lg backdrop-blur-lg",
    "top-[calc(env(safe-area-inset-top,0px)+0.5rem)] md:top-4 md:right-4",
    over ? "border-hue/70" : "border-line/80",
  ].join(" ");

  const progress = resting && left !== null ? Math.min(1, Math.max(0, 1 - left / timer.seconds)) : 0;

  const content = resting ? (
    <>
      <RestRing progress={progress} over={over} />
      <span className="sr-only">{over ? "Repos terminé" : "Repos"}</span>
      <span
        aria-hidden
        className={["num text-[0.98rem] font-bold tabular-nums", over ? "text-hue" : "text-ink"].join(" ")}
      >
        {over ? BACK_TO_IT : formatClock(left!)}
      </span>
    </>
  ) : (
    <>
      <span className="ml-1.5 h-2 w-2 shrink-0 rounded-full bg-hue" aria-hidden />
      <span className="max-w-[9rem] truncate text-[0.84rem] font-semibold">{session.name}</span>
    </>
  );

  if (onSessionScreen) {
    return (
      <div className={pill}>
        {content}
        <button
          type="button"
          onClick={timer.stop}
          className="hit h-8 rounded-full bg-surface-high px-3 text-[0.78rem] font-semibold text-ink active:opacity-70"
        >
          Passer
        </button>
      </div>
    );
  }

  return (
    <Link
      to={resumePath(session.id)}
      aria-label={`Reprendre ${session.name}${pending ? ` · ${pending}` : ""}`}
      className={`${pill} active:opacity-80`}
    >
      {content}
      {pending && (
        <span className="shrink-0 rounded-full bg-accent px-2 py-0.5 text-[0.72rem] font-semibold whitespace-nowrap text-accent-ink">
          {pending}
        </span>
      )}
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-high">
        <ArrowRight size={15} className="text-ink-soft" aria-hidden />
      </span>
    </Link>
  );
}

function formatClock(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
